/** AssociationAutocomplete — a searchable Trade Association picker for
 *  the credential-verification (`/register`) crew flow.
 *
 *  Replaces the fat `<Select>` that was choking as the ASSOCIATIONS
 *  registry grew past 40+ entries. Now the user types their initials
 *  (e.g. "FWICE") or a name fragment ("Editors Guild") and picks from
 *  the filtered list — with a permanently pinned "Not Registered"
 *  row at the bottom for crew who don't belong to any body.
 *
 *  Rows show:   `INITIALS · Full Name · City, State`
 *  Emits:       `{ id, name }`  via `onPick`.
 *  Opt-out:     picking Not Registered emits `{ id: "not_registered", name: "" }`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { BadgeCheck, Search, Users, MapPin } from "lucide-react";

export default function AssociationAutocomplete({
  value,          // currently-selected id
  onPick,         // ({id, name}) => void
  testidPrefix = "assoc",
  departmentKey, // optional — when set, filter to unions relevant to that department
}) {
  const [associations, setAssociations] = useState([]);
  const [q, setQ]         = useState("");
  const [open, setOpen]   = useState(false);
  const [loading, setLoading] = useState(true);
  const wrap = useRef(null);

  // Fetch the registry once. Client-side filter after that — <100 items
  // even after the FWICE 32-affiliate seed lands.
  // When a departmentKey is provided, fetch from the filtered endpoint
  // so the picker only shows bodies relevant to that craft.
  useEffect(() => {
    setLoading(true);
    const url = departmentKey
      ? `/meta/associations-for-department?department_key=${encodeURIComponent(departmentKey)}`
      : "/meta/associations";
    api.get(url)
       .then(({ data }) => {
         // Endpoint can return either a raw array OR the geo-split object
         // OR the department-filter object.
         let list;
         if (Array.isArray(data)) list = data;
         else if (Array.isArray(data.associations)) list = data.associations;
         else list = [...(data.local || []), ...(data.others || [])];
         setAssociations(list);
       })
       .catch(() => setAssociations([]))
       .finally(() => setLoading(false));
  }, [departmentKey]);

  // Click-outside closes the panel.
  useEffect(() => {
    const h = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return associations;
    return associations.filter((a) => {
      const initials = (a.name  || "").toLowerCase();
      const full     = (a.full  || "").toLowerCase();
      const city     = (a.city  || "").toLowerCase();
      const state    = (a.state || "").toLowerCase();
      return initials.includes(needle) ||
             full.includes(needle)     ||
             city.includes(needle)     ||
             state.includes(needle);
    });
  }, [q, associations]);

  // Group filtered results by "City · STATE" so a Chennai crew spots
  // Kollywood bodies without scrolling past 40 Bombay entries. Cities
  // are ordered by count DESC (largest cluster first) with an
  // alphabetical tiebreaker so it's deterministic.
  //
  // Chapter/wing rows (e.g. `fwice_north`, `eema_west_gj`) are seeded
  // with their real home-city so they group naturally — no virtual
  // mirroring needed.
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const a of filtered) {
      const city  = (a.city  || "").trim();
      const state = (a.state || "").trim();
      const label = city
        ? (state ? `${city} · ${state}` : city)
        : "Other / Unlisted";
      if (!buckets.has(label)) buckets.set(label, []);
      buckets.get(label).push(a);
    }
    return [...buckets.entries()]
      .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
      .map(([label, items]) => ({ label, items }));
  }, [filtered]);

  const selected = value === "not_registered"
    ? { id: "not_registered", name: "Not Registered", full: "Independent / Unaffiliated" }
    : associations.find((a) => a.id === value);

  // Selected → chip mode
  if (selected && !open) {
    return (
      <div className="flex items-center gap-2 h-11 px-3 rounded-md border border-white/15 bg-black/40"
           data-testid={`${testidPrefix}-chip`}>
        {selected.id === "not_registered"
          ? <Users size={14} className="text-white/50" />
          : <BadgeCheck size={14} className="text-[#F5B82E]" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate">
            <span className="font-semibold">{selected.name}</span>
            {selected.full && selected.full !== selected.name && (
              <span className="ml-1.5 text-white/50 text-[11px]">· {selected.full}</span>
            )}
          </div>
          {selected.city && (
            <div className="text-[10px] text-white/40 truncate">
              <MapPin size={9} className="inline mr-1" />
              {selected.city}{selected.state ? `, ${selected.state}` : ""}
            </div>
          )}
        </div>
        <button type="button"
                onClick={() => { setQ(""); setOpen(true); }}
                className="text-[10px] tracking-[0.22em] uppercase text-white/50 hover:text-white"
                data-testid={`${testidPrefix}-change`}>
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={wrap}>
      <div className="flex items-center gap-2 h-11 px-3 rounded-md border border-white/15 bg-black/40 focus-within:border-[#F5B82E]/60">
        <Search size={14} className="text-white/40" />
        <input
          data-testid={`${testidPrefix}-search`}
          type="text"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder={loading ? "Loading associations…" : "Type initials (e.g. FWICE) or full name…"}
          className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/35"
          autoComplete="off"
        />
      </div>

      {open && (
        <div className="absolute left-0 right-0 mt-1 z-30 max-h-72 overflow-hidden rounded-lg border border-white/12 bg-[#0c0c0e] shadow-xl"
             data-testid={`${testidPrefix}-dropdown`}>
          {/* Scrolling results area */}
          <div className="max-h-56 overflow-y-auto"
               data-testid={`${testidPrefix}-results`}>
            {loading && (
              <div className="px-3 py-3 text-[11px] tracking-[0.22em] uppercase text-white/50">Loading…</div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="px-3 py-3 text-[11px] tracking-[0.22em] uppercase text-white/50"
                   data-testid={`${testidPrefix}-empty`}>
                No match for &ldquo;{q}&rdquo;. Try initials or pick <span className="text-amber-300">Not Registered</span> below.
              </div>
            )}
            {!loading && grouped.map(({ label, items }) => (
              <div key={label} data-testid={`${testidPrefix}-group-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                <div className="sticky top-0 z-10 px-3 py-1.5 text-[9px] tracking-[0.28em] uppercase font-semibold text-[#F5B82E]/85 bg-[#0c0c0e]/95 backdrop-blur border-b border-white/8 flex items-center gap-1.5">
                  <MapPin size={9} />
                  {label}
                  <span className="ml-auto text-white/35 font-normal tracking-[0.16em] normal-case">
                    {items.length}
                  </span>
                </div>
                {items.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      onPick({ id: a.id, name: a.name });
                      setOpen(false);
                      setQ("");
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-2 border-b border-white/5 last:border-0"
                    data-testid={`${testidPrefix}-opt-${a.id}`}>
                    <BadgeCheck size={13} className="text-[#F5B82E] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">
                        <span className="font-semibold tracking-wide">{a.name}</span>
                        {a.full && a.full !== a.name && (
                          <span className="ml-1.5 text-white/55 text-[11px]">· {a.full}</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* Sticky footer — always visible "Not Registered" opt-out */}
          <button
            type="button"
            onClick={() => {
              onPick({ id: "not_registered", name: "Not Registered" });
              setOpen(false);
              setQ("");
            }}
            className="w-full text-left px-3 py-2.5 bg-amber-500/[0.08] hover:bg-amber-500/15 border-t border-amber-500/30 flex items-center gap-2"
            data-testid={`${testidPrefix}-opt-not-registered`}>
            <Users size={13} className="text-amber-300 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-amber-200">
                <span className="font-semibold">Not Registered</span>
                <span className="ml-1.5 text-amber-200/60 text-[11px]">· Independent / Not affiliated to any body</span>
              </div>
              <div className="text-[10px] text-amber-200/60 truncate">
                Producers can still hire you — your profile will read NON-MEMBER.
              </div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
